import { motion, AnimatePresence } from 'framer-motion';
import { Home, ChevronLeft, ChevronRight, Mail, Users, Settings, Armchair } from 'lucide-react';
import type { ReactNode } from 'react';
import { Logo } from '../branding/Logo';

type TabId = 'home' | 'invites' | 'guests' | 'table-plan' | 'settings';

interface SidebarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

interface NavItemProps {
  icon: ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  collapsed: boolean;
}

function NavItem({ icon, label, isActive, onClick, collapsed }: NavItemProps) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={`
        w-full flex items-center py-3 rounded-lg transition-colors text-left
        whitespace-nowrap overflow-hidden
        ${isActive
          ? 'bg-primary/10 text-primary'
          : 'text-text-secondary hover:bg-background-elevated hover:text-text-primary'
        }
      `}
    >
      {/* Fixed-width icon container - always centered in collapsed width */}
      <span className={`w-12 flex-shrink-0 flex justify-center ${isActive ? 'text-primary' : 'text-text-secondary'}`}>
        {icon}
      </span>
      <span
        className={`font-medium transition-opacity duration-150 ${
          collapsed ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {label}
      </span>
    </button>
  );
}

export function Sidebar({ activeTab, onTabChange, collapsed, onCollapsedChange }: SidebarProps) {
  const navItems: { id: TabId; icon: ReactNode; label: string }[] = [
    { id: 'home', icon: <Home size={22} />, label: 'Home' },
    { id: 'invites', icon: <Mail size={22} />, label: 'Invites' },
    { id: 'guests', icon: <Users size={22} />, label: 'Guests' },
    { id: 'table-plan', icon: <Armchair size={22} />, label: 'Table Plan' },
    { id: 'settings', icon: <Settings size={22} />, label: 'Settings' },
  ];

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 72 : 256 }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
        // Delay shrink until text has faded out
        delay: collapsed ? 0.15 : 0
      }}
      className="fixed left-0 top-0 h-full bg-background-card border-r border-border flex flex-col overflow-hidden z-40"
    >
      {/* Logo */}
      <div className="h-14 border-b border-border overflow-hidden flex items-center">
        <motion.div
          className="flex items-center w-full"
          initial={false}
          animate={{ paddingLeft: collapsed ? 16 : 56, paddingRight: 12 }}
          transition={{ duration: 0.3, ease: 'easeInOut', delay: collapsed ? 0.15 : 0 }}
        >
          <div className="flex-shrink-0">
            <Logo className="h-10 w-10" iconOnly />
          </div>
          <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div
                key="logo-text"
                initial={{ x: -20, opacity: 0, width: 0, marginLeft: 0 }}
                animate={{ x: 0, opacity: 1, width: 'auto', marginLeft: 8 }}
                exit={{ x: -20, opacity: 0, width: 0, marginLeft: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <Logo textOnly className="h-7 whitespace-nowrap" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => (
          <NavItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            isActive={activeTab === item.id}
            onClick={() => onTabChange(item.id)}
            collapsed={collapsed}
          />
        ))}
      </nav>

      {/* Collapse Toggle */}
      <div className="p-3 border-t border-border">
        <button
          onClick={() => onCollapsedChange(!collapsed)}
          className="w-full flex items-center justify-center py-2 rounded-lg text-text-secondary hover:bg-background-elevated hover:text-text-primary transition-colors whitespace-nowrap overflow-hidden"
        >
          <span className="flex-shrink-0">
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </span>
          <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.span
                key="collapse-text"
                initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                animate={{ width: 'auto', opacity: 1, marginLeft: 8 }}
                exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="text-sm overflow-hidden"
              >
                Collapse
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );
}

export { type TabId };
